import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

export type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
  theme: 'light',
  resolvedTheme: 'light',
  setTheme: () => {},
  toggleTheme: () => {}
});

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, updateUserPreferences } = useAuth?.() || {};
  const [theme, setThemeState] = useState<Theme>('light');
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>('light');
  const [themeLoaded, setThemeLoaded] = useState(false);

  // Helper to get system theme
  const getSystemTheme = useCallback(() => {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  }, []);

  // Load theme from localStorage or user preferences
  useEffect(() => {
    let initialTheme: Theme = 'light';
    try {
      const userTheme = (user && 'preferences' in user && (user.preferences as any)?.theme) as Theme | undefined;
      if (userTheme && (userTheme === 'light' || userTheme === 'dark' || userTheme === 'system')) {
        initialTheme = userTheme;
      } else {
      const savedTheme = localStorage.getItem('theme') as Theme;
        if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark' || savedTheme === 'system')) {
          initialTheme = savedTheme;
        }
      }
    } catch (e) {
      console.error('Failed to load theme from localStorage or user', e);
    }
    setThemeState(initialTheme);
    setThemeLoaded(true);
  }, [user]);

  // Apply theme to document and persist
  useEffect(() => {
    let appliedTheme: 'light' | 'dark';
    if (theme === 'system') {
      appliedTheme = getSystemTheme();
    } else {
      appliedTheme = theme;
    }
    setResolvedTheme(appliedTheme);
    try {
      localStorage.setItem('theme', theme);
      if (appliedTheme === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    } catch (e) {
      console.error('Failed to save theme to localStorage', e);
    }
    // Sync to Firestore if logged in
    if (user && updateUserPreferences) {
      updateUserPreferences({ theme });
    }
  }, [theme, user, updateUserPreferences, getSystemTheme]);

  // Listen for system theme changes if 'system' is selected
  useEffect(() => {
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      setResolvedTheme(mq.matches ? 'dark' : 'light');
      if (mq.matches) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    };
    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, [theme]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  const toggleTheme = () => {
    setThemeState(prev => {
      if (prev === 'light') return 'dark';
      if (prev === 'dark') return 'light';
      // If system, toggle to light
      return 'light';
    });
  };

  if (!themeLoaded) {
    // Optionally, show a splash or loading spinner here
    return <div style={{ minHeight: '100vh', background: '#fff' }} />;
  }

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  return useContext(ThemeContext);
}; 